import {
  FC,
  useState,
  useRef,
  forwardRef,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useImperativeHandle,
  MutableRefObject,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import classNames from 'classnames/bind';
import { FAIcon } from '../../Icons/Icons';
import { MLPVersion, Theme } from '../../../types/enums';
import DropDown, { DropDownType } from '../../DropDown/DropDown';
import { Data } from '../../DropDown/types/propsType';
import { useTranslation } from '../../Translation/TranslationProvider';
import { DatePickerType, TimePicker } from '../DatePicker';
import { CalendarYearDropdownType, TimePickerModal } from '../types/enums';
import RawModal, { TextAlign } from '../../Modal/RawModal';
import modalStyles from '../../Modal/Modal.module.scss';
import Button, { ButtonSize, ButtonStyles, ButtonTypes } from '../../Button/Button';
import {
  getTimeList,
  newYearDirection,
  newMonthDirection,
  selectedRangeLabel,
  calendarDaysMapping,
  getDateInfo,
  getCeilingTime,
  getYearList,
  getPrimaryBtnProp,
  timeFormat12to24Converter,
  timeInFormat,
  getModifiedTimeInDate,
} from './CalendarHelper';
import {
  CalendarProps,
  RefType,
  CalendarTheme,
  NavigationButtonDisable,
  CalendarMonthYearComponentPropType,
} from './../types/propTypes';
import styles from './Calendar.module.scss';

const cx = classNames.bind(styles);
// Create component as per showYearDropdown
const calendarMonthYearComponent = ({
  displayMonth,
  displayYear,
  showYearDropdown,
  yearDropdownData,
  yearDropdownConfigData,
  yearList,
  navigate,
}: CalendarMonthYearComponentPropType) => (
  <span className={styles['navigate-year-dropdown-container']}>
    {`${displayMonth} ${!showYearDropdown ? displayYear : ''}`}
    {showYearDropdown && (
      <DropDown
        className={styles['navigate-year-dropdown']}
        name="year-picker"
        dropdownLabel={displayYear}
        value={Number(displayYear)}
        dropDownType={DropDownType.INLINE}
        data={showYearDropdown === CalendarYearDropdownType.CUSTOM ? yearDropdownData : yearList}
        configData={{
          label:
            showYearDropdown === CalendarYearDropdownType.CUSTOM ? (yearDropdownConfigData?.label as string) : 'name',
          onSelect: (item: Data) => {
            showYearDropdown === CalendarYearDropdownType.CUSTOM && yearDropdownConfigData?.onSelect(item);
            navigate(undefined, undefined, item.name as number);
          },
        }}
      />
    )}
  </span>
);
function calendarBodyMapping({
  currentTimePicker,
  startTimeBody,
  endTimeBody,
  calendarMainBody,
}: {
  currentTimePicker: ReactNode;
  startTimeBody: JSX.Element;
  endTimeBody: JSX.Element;
  calendarMainBody: JSX.Element;
}) {
  const { START_TIME, END_TIME } = TimePickerModal;
  switch (currentTimePicker) {
    case START_TIME:
      return startTimeBody;
    case END_TIME:
      return endTimeBody;
    default:
      return calendarMainBody;
  }
}
// Calendar Component
const Calendar: FC<CalendarProps> = forwardRef<HTMLDivElement | RefType, CalendarProps>(
  (
    {
      showFullCalendar = true,
      showTime = false,
      onChange,
      disabledDates,
      disabledPastDates,
      timeLabel,
      timeLabelStart,
      timeLabelEnd,
      selectRange = false,
      onNavigate,
      value,
      valueTo,
      startTimeValue,
      endTimeValue,
      ariaLabel,
      calendarTitle = '',
      calendarHeading = '',
      show4WeeksOnly = false,
      type = DatePickerType.DROP_DOWN,
      is24HoursFormat = true,
      disablePastTime = false,
      disableDate,
      disableTime,
      theme = Theme.ACTION,
      navigationButtonDisable = { disableLeft: false, disableRight: false },
      scheduleEventOnDates,
      onClose,
      showYearDropdown,
      yearDropdownData,
      yearDropdownConfigData,
      useReactPortal,
      portalContainerId,
    },
    ref,
  ) => {
    const t = useTranslation();

    const [calendarModalDateTime, setCalendarModalDateTime] = useState(value);
    const [calendarInterimDateTime, setCalendarInterimDateTime] = useState(value);
    const [calendarInterimEndDateTime, setCalendarInterimEndDateTime] = useState(valueTo);
    const [calendarModalEndDateTime, setCalendarModalEndDateTime] = useState(valueTo);
    // list of 100 year from 1970
    const defaultYearList = useMemo(() => getYearList(), []);
    const [startTime, setStartTime] = useState<string | undefined>(getCeilingTime(is24HoursFormat, startTimeValue));
    const [endTime, setEndTime] = useState<string | undefined>(getCeilingTime(is24HoursFormat, endTimeValue));
    const [currentTimePicker, setCurrentTimePicker] = useState(TimePickerModal.NONE);
    const dateTimePickerRef = useRef() as MutableRefObject<HTMLDivElement>;
    useImperativeHandle(ref, () => dateTimePickerRef.current);

    const ariaLabelValue = ariaLabel || t('nextgen.Calendar.ariaLabel');

    // update value if props updated after render
    useEffect(() => {
      setCalendarModalDateTime(value);
      setCalendarInterimDateTime(value);
      if (showTime) {
        const currentStartTime = timeInFormat(is24HoursFormat, startTimeValue);
        setStartTime(currentStartTime);
      }
      if (selectRange) {
        setCalendarInterimEndDateTime(valueTo);
        setCalendarModalEndDateTime(valueTo);
        if (showTime) {
          const currentEndTime = timeInFormat(is24HoursFormat, endTimeValue);
          setEndTime(currentEndTime);
        }
      }
    }, [value, valueTo, startTimeValue, endTimeValue]);
    const { date, year, month } = getDateInfo(
      show4WeeksOnly,
      calendarInterimDateTime,
      yearDropdownConfigData?.defaultYear,
    );
    const days = t('nextgen.Calendar.days').split(',');
    const months = t('nextgen.Calendar.months').split(',');

    const handleCalendarTimePicker = (isEndTime = false) => {
      setCurrentTimePicker(isEndTime ? TimePickerModal.END_TIME : TimePickerModal.START_TIME);
    };
    const navigate = (
      event?: ReactKeyboardEvent | ReactMouseEvent,
      direction?: 'left' | 'right',
      currentYear?: number,
    ) => {
      if (direction) {
        const newYear = newYearDirection(direction, year, month);
        const newMonth = newMonthDirection(direction, month);
        setCalendarInterimDateTime(new Date(newYear, newMonth));
      }
      if (currentYear) {
        setCalendarInterimDateTime(new Date(currentYear, month));
      }
      onNavigate?.(event);
    };
    const onDateTimeChange = (
      date?: Date,
      isMouseOverEvent = false,
      currentStartTime?: string,
      currentEndTime?: string,
      event?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
    ) => {
      const returnObj = {
        dateTime: date,
        todate: valueTo,
        startTime: currentStartTime,
        endTime: currentEndTime,
      };
      if (selectRange && value && date) {
        if (value.getTime() < date.getTime()) {
          if (!valueTo && isMouseOverEvent) {
            setCalendarInterimEndDateTime(date);
          } else if (!valueTo) {
            returnObj.dateTime = value;
            returnObj.todate = date;
          } else {
            returnObj.dateTime = date;
            returnObj.todate = undefined;
            !isMouseOverEvent && setCalendarInterimDateTime(date);
            setCalendarInterimEndDateTime(undefined);
          }
        } else {
          returnObj.dateTime = date;
          returnObj.todate = undefined;
          !isMouseOverEvent && setCalendarInterimDateTime(date);
          setCalendarInterimEndDateTime(undefined);
        }
      }
      if (returnObj.dateTime && returnObj.startTime) {
        const [hour, minute] = timeFormat12to24Converter(returnObj.startTime);
        returnObj.dateTime.setHours(hour);
        returnObj.dateTime.setMinutes(minute);
      }
      if (returnObj.todate && returnObj.endTime) {
        const [hour, minute] = timeFormat12to24Converter(returnObj.endTime);
        returnObj.todate.setHours(hour);
        returnObj.todate.setMinutes(minute);
      }

      const { modifiedDate, modifiedToDate } = getModifiedTimeInDate(
        is24HoursFormat,
        returnObj.dateTime,
        returnObj.todate,
        returnObj.startTime,
        returnObj.endTime,
      );

      !isMouseOverEvent && onChange?.(modifiedDate, modifiedToDate, returnObj.startTime, returnObj.endTime, event);
    };
    // Triggers when APPLY button click
    const handlePrimaryBtnClick = (event: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent) => {
      if (currentTimePicker === TimePickerModal.NONE) {
        if (calendarModalDateTime && startTime) {
          const [hour, minute] = timeFormat12to24Converter(startTime);
          calendarModalDateTime.setHours(hour);
          calendarModalDateTime.setMinutes(minute);
        }
        if (calendarModalEndDateTime && endTime) {
          const [hour, minute] = timeFormat12to24Converter(endTime);
          calendarModalEndDateTime.setHours(hour);
          calendarModalEndDateTime.setMinutes(minute);
        }
        onChange?.(calendarModalDateTime, calendarModalEndDateTime, startTime, endTime, event);
        handleCancelBtnClick();
      } else {
        setCurrentTimePicker(TimePickerModal.NONE);
      }
    };
    // Triggers when CLEAR button click
    const handleDateTimeClear = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      setCalendarModalDateTime(undefined);
      setCalendarModalEndDateTime(undefined);
      setCalendarInterimDateTime(undefined);
      setCalendarInterimEndDateTime(undefined);

      setStartTime(undefined);
      setEndTime(undefined);
      onChange?.(undefined, undefined, undefined, undefined, event);
      handleCancelBtnClick();
    };
    // Intermediate function, called when calendar is in modal
    const handleDateTimeUpdate = (
      event: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
      selectedDate: Date,
      isMouseOverEvent = false,
      currentStartTime?: string,
      currentEndTime?: string,
      isApplyClick?: boolean,
    ) => {
      if (type !== DatePickerType.MODAL || isApplyClick) {
        onDateTimeChange(selectedDate, isMouseOverEvent, currentStartTime, currentEndTime, event);
      } else if (selectRange) {
        // handle range in modal
        const tempDate = calendarModalDateTime || date;
        if (tempDate.getTime() < selectedDate.getTime()) {
          if (!calendarModalEndDateTime && isMouseOverEvent) {
            setCalendarInterimEndDateTime(selectedDate);
          } else if (!calendarModalEndDateTime) {
            setCalendarModalEndDateTime(selectedDate);
            setCalendarInterimEndDateTime(undefined);
          } else if (!isMouseOverEvent) {
            setCalendarModalEndDateTime(undefined);
            setCalendarInterimEndDateTime(undefined);
            setCalendarModalDateTime(selectedDate);
          }
        } else if (!isMouseOverEvent) {
          setCalendarModalDateTime(selectedDate);
          setCalendarModalEndDateTime(undefined);
          setCalendarInterimEndDateTime(undefined);
        }
      } else {
        !isMouseOverEvent && setCalendarModalDateTime(selectedDate);
      }
    };
    const handleOnStartTimeChange = (time: string) => {
      const { modifiedDate, modifiedToDate } = getModifiedTimeInDate(is24HoursFormat, value, valueTo, time, endTime);
      setStartTime(time);
      onChange?.(modifiedDate, modifiedToDate, time, endTime);
    };
    const handleOnEndTimeChange = (time: string) => {
      const { modifiedDate, modifiedToDate } = getModifiedTimeInDate(is24HoursFormat, value, valueTo, startTime, time);
      setEndTime(time);
      onChange?.(modifiedDate, modifiedToDate, startTime, time);
    };
    // Triggers when CANCEL button click
    const handleCancelBtnClick = () => {
      if (currentTimePicker === TimePickerModal.NONE) {
        setCalendarModalDateTime(value);
        setCalendarModalEndDateTime(valueTo);
        setCalendarInterimDateTime(value);
        setCalendarInterimEndDateTime(valueTo);
        setStartTime(getCeilingTime(is24HoursFormat, startTimeValue));
        setEndTime(getCeilingTime(is24HoursFormat, endTimeValue));
        onClose?.();
      } else {
        setStartTime(getCeilingTime(is24HoursFormat, startTimeValue));
        setEndTime(getCeilingTime(is24HoursFormat, endTimeValue));
        setCurrentTimePicker(TimePickerModal.NONE);
      }
    };
    // update primary button status
    const isTimePickerModalType = type === DatePickerType.MODAL;
    let dayMapPayload = {
      baseDate: value,
      baseEndDate: valueTo,
      intermediateDate: calendarInterimDateTime,
      intermediateEndDate: calendarInterimEndDateTime,
      showFullCalendar,
      show4WeeksOnly,
      handleDateTimeUpdate,
      selectRange,
      scheduleEventOnDates,
      disableDate,
      disabledDates,
      disabledPastDates,
      ariaLabel: ariaLabelValue,
      defaultYear: yearDropdownConfigData?.defaultYear,
    };
    if (isTimePickerModalType) {
      dayMapPayload = {
        ...dayMapPayload,
        baseDate: calendarModalDateTime,
        baseEndDate: calendarModalEndDateTime,
      };
    }

    //generate dates of current month in viewport
    const dates = useMemo(() => calendarDaysMapping(dayMapPayload), [dayMapPayload]);
    const timeList = useMemo(
      () => getTimeList(15, !is24HoursFormat, 0, disablePastTime, value),
      [is24HoursFormat, disablePastTime, value],
    );
    const { btnTitle, btnCB, isDisable } = getPrimaryBtnProp(
      value,
      calendarModalDateTime,
      valueTo,
      calendarModalEndDateTime,
      startTimeValue,
      startTime,
      endTimeValue,
      endTime,
      selectRange,
      showTime,
      handlePrimaryBtnClick,
      handleDateTimeClear,
      currentTimePicker,
    );
    const CClassname = cx('calendar-container', { 'show-calendar-title-only': Boolean(calendarTitle) }, [theme]);
    const DateClassname = cx({ 'date-row-range': selectRange }, 'day-sequence');
    const TimeClassname = cx('time-container', { 'modal-time-container': isTimePickerModalType });

    // ui components
    const modalHeader = (
      <div className={styles['calendar-modal-header']}>
        <section
          className={`${modalStyles['modal-headline']} ${modalStyles[`modal-headline-${TextAlign.CENTER}`]}`}
          data-testid="modal-healine-test-id"
        >
          {calendarHeading}
        </section>
        <div className={modalStyles['modal-icon-section']}>
          <FAIcon
            className={styles['icon-close']}
            data-testid="close-icon-test-id"
            iconName="xmark"
            theme={Theme.BLACK}
            onClick={() => {
              setCurrentTimePicker(TimePickerModal.NONE);
              onClose?.();
            }}
          />
        </div>
      </div>
    );
    const modalFooter = (
      <>
        <Button
          title={btnTitle}
          type={ButtonTypes.BUTTON}
          theme={Theme.BLACK}
          size={ButtonSize.SMALL}
          onClick={btnCB}
          disabled={isDisable}
          data-testid="primary-button-test-id"
        />
        <Button
          title="Cancel"
          type={ButtonTypes.BUTTON}
          theme={Theme.BLACK}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          onClick={handleCancelBtnClick}
          data-testid="secondary-button-test-id"
        />
      </>
    );
    const startTimeLabel = selectedRangeLabel(selectRange, timeLabelStart, timeLabel);
    const calendarMainBody = (
      <div className={CClassname} data-testid="calendar-container" ref={dateTimePickerRef} aria-label={ariaLabelValue}>
        <div className={styles['month-container']}>
          {calendarTitle ? (
            <span className={styles['calendar-title']} data-testid="calendar-title-testid">
              {calendarTitle}
            </span>
          ) : (
            <>
              <span className={styles['navigation-month-year']}>
                {calendarMonthYearComponent({
                  displayMonth: months[month],
                  displayYear: `${year}`,
                  showYearDropdown,
                  yearDropdownData,
                  yearDropdownConfigData,
                  yearList: defaultYearList,
                  navigate,
                })}
              </span>
              <span className={styles['navigation-container']}>
                <FAIcon
                  iconName="arrow-left"
                  className={`${styles['navigation-left']} ${
                    navigationButtonDisable.disableLeft ? styles['disabled-nav-btn'] : ''
                  }`}
                  data-testid="navigation-left"
                  onClick={(e) => navigate(e, 'left')}
                />
                <FAIcon
                  iconName="arrow-right"
                  className={`${styles['navigation-right']} ${
                    navigationButtonDisable.disableRight ? styles['disabled-nav-btn'] : ''
                  }`}
                  data-testid="navigation-right"
                  onClick={(e) => navigate(e, 'right')}
                />
              </span>
            </>
          )}
        </div>

        <div className={styles['dates-container']}>
          {showFullCalendar && (
            <div className={DateClassname}>
              {days.map((day) => (
                <span className={`${styles['day-cell']} ${styles['heading-cell']}`} key={day}>
                  {day}
                </span>
              ))}
            </div>
          )}
          {dates}
        </div>
        {showFullCalendar && showTime && (
          <div className={TimeClassname}>
            {startTimeLabel ? <FAIcon iconName="clock" className={styles['time-icon']} /> : ''}
            <span className={styles['time-label']} data-testid="time-label">
              {startTimeLabel}
            </span>
            <span className={styles['time-dropdown']}>
              {isTimePickerModalType ? (
                <>
                  <span
                    role="button"
                    className={`${styles['time-value-label']}`}
                    onClick={() => handleCalendarTimePicker()}
                    onKeyDown={() => handleCalendarTimePicker()}
                    aria-label="select start time"
                    tabIndex={0}
                    data-testid="start-time-element"
                  >
                    {startTime || 'Select start time'}
                  </span>
                  <div className={styles['time-value-divider']}></div>
                </>
              ) : (
                <DropDown
                  className={`${styles['dropdown-box']} ${styles['base-dropdown-result']} ${styles['open-dropdown-result']}`}
                  name="time-picker"
                  data={timeList}
                  configData={{
                    label: 'name',
                    onSelect: (item: Data) => handleOnStartTimeChange(item.name as string),
                    disableItemKey: 'isDisabled',
                  }}
                  value={startTime || startTimeValue}
                  mlpVersion={MLPVersion.ONE}
                />
              )}
            </span>
          </div>
        )}
        {showFullCalendar && showTime && selectRange && (
          <div className={TimeClassname}>
            {timeLabelEnd ? <FAIcon iconName={'clock'} className={styles['time-icon']} /> : ''}
            <span className={styles['time-label']} data-testid="time-label">
              {timeLabelEnd || 'Select end time'}
            </span>
            <span className={styles['time-dropdown']}>
              {isTimePickerModalType ? (
                <>
                  <span
                    role="button"
                    className={`${styles['time-value-label']}`}
                    onClick={() => handleCalendarTimePicker(true)}
                    onKeyDown={() => handleCalendarTimePicker(true)}
                    aria-label="select end time"
                    tabIndex={0}
                    data-testid="end-time-element"
                  >
                    {endTime || 'Select end time'}
                  </span>
                  <div className={styles['time-value-divider']}></div>
                </>
              ) : (
                <DropDown
                  className={`${styles['dropdown-box']} ${styles['base-dropdown-result']} ${styles['open-dropdown-result']}`}
                  name="time-picker"
                  data={timeList}
                  configData={{
                    label: 'name',
                    onSelect: (item: Data) => handleOnEndTimeChange(item.name as string),
                  }}
                  value={endTime || endTimeValue}
                  mlpVersion={MLPVersion.ONE}
                />
              )}
            </span>
          </div>
        )}
      </div>
    );
    const startTimeBody = (
      <TimePicker
        disablePastTime={disablePastTime}
        title="Select start time"
        onTimeChange={setStartTime}
        value={startTime}
        is24HoursFormat={is24HoursFormat}
        disableTime={(time: string) => disableTime?.(time, calendarModalDateTime || date) || false}
      />
    );
    const endTimeBody = (
      <TimePicker
        disablePastTime={disablePastTime}
        title="Select end time"
        onTimeChange={setEndTime}
        value={endTime}
        disableTime={(time: string) => disableTime?.(time, calendarModalEndDateTime || date) || false}
        is24HoursFormat={is24HoursFormat}
      />
    );

    if (isTimePickerModalType) {
      return (
        <RawModal
          id="calendar_modal"
          header={modalHeader}
          footer={modalFooter}
          onClose={onClose}
          textAlign={TextAlign.LEFT}
          useReactPortal={useReactPortal}
          portalContainerId={portalContainerId}
        >
          {calendarBodyMapping({
            currentTimePicker,
            startTimeBody,
            endTimeBody,
            calendarMainBody,
          })}
        </RawModal>
      );
    }
    return calendarBodyMapping({
      currentTimePicker,
      startTimeBody,
      endTimeBody,
      calendarMainBody,
    });
  },
);
Calendar.displayName = 'Calendar';
export type { CalendarTheme, NavigationButtonDisable };
export { CalendarYearDropdownType };
export default Calendar;
